import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Typography, Button, Statistic, Tag, Space, Tabs, Table,
  Modal, Form, Input, Select, message, Alert, Spin,
  Divider, Empty, Result,
} from 'antd';
import {
  GoogleOutlined, RocketOutlined, ThunderboltOutlined, AimOutlined,
  MailOutlined, TeamOutlined, GlobalOutlined, AppstoreOutlined,
  CheckCircleOutlined, CloseCircleOutlined, BulbOutlined,
  SendOutlined, LinkOutlined,
  BarChartOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { googleAdsAPI, landingPageAPI, emailCampaignAPI, contactsAPI, scenarioAPI } from '../services/api';
import { Line, Column } from '@ant-design/charts';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

// Performance metric formatting helpers
const fmtNum = (v) => (v == null ? 0 : Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n >= 1000 ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : n.toFixed(2);
};
const fmtPct = (v) => (v == null ? '0%' : `${Number(v).toFixed(2)}%`);

// Source badge colors for campaigns created from different websites
const SOURCE_COLORS = {
  'house-ai': 'blue',
  'comboware-ai': 'geekblue',
  'other': 'default',
};
function SourceTag({ source }) {
  if (!source || source === 'other') return <Tag>other</Tag>;
  return <Tag color={SOURCE_COLORS[source] || 'purple'}>{source}</Tag>;
}

export default function GoogleAdsHubPage() {
  // State
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Module data for create-from
  const [landingPages, setLandingPages] = useState([]);
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [scenarios, setScenarios] = useState([]);

  // AI Copy Generator
  const [aiForm] = Form.useForm();
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  // Create Campaign modal
  const [createModal, setCreateModal] = useState({ open: false, type: null, sourceId: null, sourceName: '' });
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);

  // Performance reporting
  const [performance, setPerformance] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [dateRange, setDateRange] = useState('LAST_30_DAYS');
  const [sourceFilter, setSourceFilter] = useState('all');

  // ── Load Data ─────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, summaryRes] = await Promise.all([
        googleAdsAPI.getStatus(),
        googleAdsAPI.getIntegrationSummary(),
      ]);
      setStatus(statusRes);
      setSummary(summaryRes);
    } catch (e) {
      console.error(e);
    }

    // Load module data (non-blocking)
    try {
      const [lpRes, ecRes, clRes, scRes] = await Promise.all([
        landingPageAPI.list().catch(() => ({ data: [] })),
        emailCampaignAPI.list().catch(() => ({ data: [] })),
        contactsAPI.getLists().catch(() => ({ data: [] })),
        scenarioAPI.list().catch(() => ({ data: [] })),
      ]);
      setLandingPages(Array.isArray(lpRes.data) ? lpRes.data : (Array.isArray(lpRes) ? lpRes : []));
      setEmailCampaigns(Array.isArray(ecRes.data) ? ecRes.data : (Array.isArray(ecRes) ? ecRes : []));
      setContactLists(Array.isArray(clRes.data) ? clRes.data : (Array.isArray(clRes) ? clRes : []));
      setScenarios(Array.isArray(scRes.data) ? scRes.data : (Array.isArray(scRes) ? scRes : []));
    } catch (e) {
      console.error('Failed to load module data', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Performance Reporting ─────────────────────────────────────
  const loadPerformance = useCallback(async () => {
    setPerfLoading(true);
    try {
      const res = await googleAdsAPI.getPerformance(dateRange);
      setPerformance(res);
    } catch (e) {
      console.error(e);
      setPerformance({ ok: false, error: 'Failed to load performance data' });
    }
    setPerfLoading(false);
  }, [dateRange]);

  useEffect(() => { loadPerformance(); }, [loadPerformance]);

  // ── AI Copy Generation ────────────────────────────────────────
  const handleAIGenerate = async (values) => {
    setAiGenerating(true);
    setAiResult(null);
    try {
      const res = await googleAdsAPI.aiGenerateCopy(values);
      if (res.ok) {
        setAiResult(res);
        message.success('AI ad copy generated');
      } else {
        message.error(res.error || 'Generation failed');
      }
    } catch (e) {
      message.error('Request failed');
    }
    setAiGenerating(false);
  };

  // ── Create Campaign ───────────────────────────────────────────
  const handleCreate = async () => {
    setCreating(true);
    setCreateResult(null);
    try {
      let res;
      const { type, sourceId } = createModal;
      if (type === 'landing_page') {
        res = await googleAdsAPI.createFromLandingPage({ landing_page_id: sourceId });
      } else if (type === 'email_campaign') {
        res = await googleAdsAPI.createFromEmailCampaign({ email_campaign_id: sourceId });
      } else if (type === 'contact_list') {
        res = await googleAdsAPI.createFromContactList({
          contact_list_id: sourceId,
          product_info: createModal.productInfo || 'SPO Platform',
        });
      } else if (type === 'scenario') {
        res = await googleAdsAPI.createFromScenario({ scenario_id: sourceId });
      }
      setCreateResult(res);
      if (res?.ok) {
        message.success('Google Ads campaign created successfully');
      } else {
        message.error(res?.error || 'Creation failed');
      }
    } catch (e) {
      message.error('Request failed');
    }
    setCreating(false);
  };

  const openCreate = (type, sourceId, sourceName) => {
    setCreateResult(null);
    setCreateModal({ open: true, type, sourceId, sourceName, productInfo: '' });
  };

  // ── Render ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, color: '#999' }}>Loading Google Ads Hub...</div>
      </div>
    );
  }

  const isConfigured = status?.configured;

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* ── Hero Header ──────────────────────────────────────── */}
      <Card
        style={{
          marginBottom: 24,
          background: 'linear-gradient(135deg, #1a73e8 0%, #0d47a1 50%, #002171 100%)',
          border: 'none',
          borderRadius: 16,
        }}
        bodyStyle={{ padding: '32px 40px' }}
      >
        <Row gutter={24} align="middle">
          <Col flex="auto">
            <Space size={16} align="center">
              <GoogleOutlined style={{ fontSize: 48, color: '#fff' }} />
              <div>
                <Title level={2} style={{ color: '#fff', margin: 0 }}>
                  Google Ads Integration Hub
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16 }}>
                  One-stop management: from AI copy generation to ad delivery, deeply connected across all platform modules
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Tag
              color={isConfigured ? 'green' : 'red'}
              icon={isConfigured ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              style={{ fontSize: 14, padding: '4px 16px' }}
            >
              {isConfigured ? 'Connected' : 'Not Configured'}
            </Tag>
          </Col>
        </Row>
      </Card>

      {/* ── Connection Alert ─────────────────────────────────── */}
      {!isConfigured && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24, borderRadius: 12 }}
          message="Google Ads is not configured"
          description={
            <span>
              Go to <a href="/marketing-tools">Marketing Tools Settings</a> to fill in your Google Ads API credentials and unlock the full feature set.
              AI copy generation works without a Google Ads account.
            </span>
          }
        />
      )}

      {/* ── Platform Integration Dashboard ───────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card hoverable style={{ borderRadius: 12, textAlign: 'center' }}>
            <Statistic
              title={<span><AppstoreOutlined /> Scenarios</span>}
              value={summary?.scenarios?.total || 0}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>/ {summary?.scenarios?.published || 0} published</Text>}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card hoverable style={{ borderRadius: 12, textAlign: 'center' }}>
            <Statistic
              title={<span><GlobalOutlined /> Landing Pages</span>}
              value={summary?.landing_pages?.total || 0}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>/ {summary?.landing_pages?.published || 0} published</Text>}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card hoverable style={{ borderRadius: 12, textAlign: 'center' }}>
            <Statistic
              title={<span><MailOutlined /> Email Campaigns</span>}
              value={summary?.email_campaigns?.total || 0}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card hoverable style={{ borderRadius: 12, textAlign: 'center' }}>
            <Statistic
              title={<span><TeamOutlined /> Contacts</span>}
              value={summary?.contacts?.total || 0}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>/ {summary?.contacts?.lists || 0} lists</Text>}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Main Tabs ────────────────────────────────────────── */}
      <Card style={{ borderRadius: 16 }}>
        <Tabs defaultActiveKey="ai" size="large">
          {/* ── Tab 1: AI Copy Generator ───────────────────────── */}
          <TabPane
            tab={<span><BulbOutlined /> AI Ad Copy Generator</span>}
            key="ai"
          >
            <Row gutter={24}>
              <Col xs={24} lg={12}>
                <Card
                  title="Enter Product Info"
                  style={{ borderRadius: 12 }}
                  extra={<Tag color="blue">Claude AI</Tag>}
                >
                  <Form form={aiForm} layout="vertical" onFinish={handleAIGenerate}>
                    <Form.Item
                      name="product_info"
                      label="Product / Service Description"
                      rules={[{ required: true, message: 'Please enter product info' }]}
                    >
                      <TextArea
                        rows={4}
                        placeholder="e.g. Cloud server management platform with one-click deployment, auto-scaling, real-time monitoring..."
                      />
                    </Form.Item>
                    <Form.Item name="target_audience" label="Target Audience (optional)">
                      <Input placeholder="e.g. SME IT managers, e-commerce business owners" />
                    </Form.Item>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="ad_type" label="Ad Type" initialValue="search">
                          <Select>
                            <Select.Option value="search">Search Ads (RSA)</Select.Option>
                            <Select.Option value="display">Display Ads</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="language" label="Language" initialValue="en">
                          <Select>
                            <Select.Option value="zh-TW">繁體中文</Select.Option>
                            <Select.Option value="en">English</Select.Option>
                            <Select.Option value="th">Thai</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<ThunderboltOutlined />}
                      loading={aiGenerating}
                      size="large"
                      block
                      style={{ background: 'linear-gradient(135deg, #1a73e8, #0d47a1)', border: 'none', height: 48 }}
                    >
                      Generate AI Ad Copy
                    </Button>
                  </Form>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card
                  title="Generated Result"
                  style={{ borderRadius: 12, minHeight: 400 }}
                  extra={aiResult && <Tag color="green">tokens: {aiResult.tokens_used}</Tag>}
                >
                  {aiGenerating ? (
                    <div style={{ textAlign: 'center', padding: 60 }}>
                      <Spin size="large" />
                      <div style={{ marginTop: 16 }}>AI is generating optimized ad copy...</div>
                    </div>
                  ) : aiResult?.ad_copy ? (
                    <div>
                      {aiResult.ad_copy.headlines && (
                        <div style={{ marginBottom: 16 }}>
                          <Text strong>Headlines ({aiResult.ad_copy.headlines.length})</Text>
                          <div style={{ marginTop: 8 }}>
                            {aiResult.ad_copy.headlines.map((h, i) => (
                              <Tag key={i} color="blue" style={{ marginBottom: 4 }}>{h}</Tag>
                            ))}
                          </div>
                        </div>
                      )}
                      {aiResult.ad_copy.descriptions && (
                        <div style={{ marginBottom: 16 }}>
                          <Text strong>Descriptions ({aiResult.ad_copy.descriptions.length})</Text>
                          {aiResult.ad_copy.descriptions.map((d, i) => (
                            <Paragraph key={i} style={{ background: '#f5f5f5', padding: '8px 12px', borderRadius: 8, marginTop: 4 }}>
                              {d}
                            </Paragraph>
                          ))}
                        </div>
                      )}
                      {aiResult.ad_copy.keywords && (
                        <div style={{ marginBottom: 16 }}>
                          <Text strong>Keywords ({aiResult.ad_copy.keywords.length})</Text>
                          <div style={{ marginTop: 8 }}>
                            {aiResult.ad_copy.keywords.map((k, i) => (
                              <Tag key={i} style={{ marginBottom: 4 }}>{k}</Tag>
                            ))}
                          </div>
                        </div>
                      )}
                      {aiResult.ad_copy.campaign_name_suggestion && (
                        <div>
                          <Text strong>Suggested campaign name: </Text>
                          <Text code>{aiResult.ad_copy.campaign_name_suggestion}</Text>
                        </div>
                      )}
                      {/* Display ad specific fields */}
                      {aiResult.ad_copy.short_headline && (
                        <div style={{ marginBottom: 8 }}>
                          <Text strong>Short headline: </Text> <Text>{aiResult.ad_copy.short_headline}</Text>
                        </div>
                      )}
                      {aiResult.ad_copy.long_headline && (
                        <div style={{ marginBottom: 8 }}>
                          <Text strong>Long headline: </Text> <Text>{aiResult.ad_copy.long_headline}</Text>
                        </div>
                      )}
                      {aiResult.ad_copy.call_to_action && (
                        <div>
                          <Text strong>CTA: </Text> <Tag color="green">{aiResult.ad_copy.call_to_action}</Tag>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Empty description="Not generated yet. Enter product info on the left and click Generate." />
                  )}
                </Card>
              </Col>
            </Row>
          </TabPane>

          {/* ── Tab 2: Landing Pages → Ads ─────────────────────── */}
          <TabPane
            tab={<span><GlobalOutlined /> Landing Pages</span>}
            key="lp"
          >
            <Alert
              type="info"
              showIcon
              icon={<RocketOutlined />}
              message="Landing Page → Google Ads"
              description="Create a Google Ads search campaign from a published Landing Page with one click — AI auto-generates optimized copy and keywords"
              style={{ marginBottom: 16, borderRadius: 12 }}
            />
            <Table
              dataSource={landingPages}
              rowKey="id"
              pagination={{ pageSize: 8 }}
              columns={[
                { title: 'ID', dataIndex: 'id', width: 60 },
                { title: 'Title', dataIndex: 'title', ellipsis: true },
                { title: 'Slug', dataIndex: 'slug', width: 150, render: v => v ? <Text code>{v}</Text> : '-' },
                {
                  title: 'Status', dataIndex: 'is_published', width: 80,
                  render: v => v ? <Tag color="green">Published</Tag> : <Tag>Draft</Tag>,
                },
                {
                  title: 'Actions', width: 180,
                  render: (_, record) => (
                    <Button
                      type="primary"
                      size="small"
                      icon={<SendOutlined />}
                      disabled={!record.is_published || !isConfigured}
                      onClick={() => openCreate('landing_page', record.id, record.title)}
                    >
                      Create Ad
                    </Button>
                  ),
                },
              ]}
              locale={{ emptyText: <Empty description="No landing pages yet. Create one first." /> }}
            />
          </TabPane>

          {/* ── Tab 3: Email Campaign → Ads ────────────────────── */}
          <TabPane
            tab={<span><MailOutlined /> Email Retargeting</span>}
            key="email"
          >
            <Alert
              type="info"
              showIcon
              icon={<AimOutlined />}
              message="Email Campaign → Google Ads Retargeting"
              description="Convert email campaign content into Google Ads to expand reach"
              style={{ marginBottom: 16, borderRadius: 12 }}
            />
            <Table
              dataSource={emailCampaigns}
              rowKey="id"
              pagination={{ pageSize: 8 }}
              columns={[
                { title: 'ID', dataIndex: 'id', width: 60 },
                { title: 'Name', dataIndex: 'name', ellipsis: true },
                { title: 'Subject', dataIndex: 'subject', ellipsis: true },
                {
                  title: 'Status', dataIndex: 'status', width: 80,
                  render: v => <Tag color={v === 'sent' ? 'green' : v === 'draft' ? 'default' : 'blue'}>{v}</Tag>,
                },
                {
                  title: 'Actions', width: 180,
                  render: (_, record) => (
                    <Button
                      type="primary"
                      size="small"
                      icon={<SendOutlined />}
                      disabled={!isConfigured}
                      onClick={() => openCreate('email_campaign', record.id, record.name)}
                    >
                      Create Ad
                    </Button>
                  ),
                },
              ]}
              locale={{ emptyText: <Empty description="No email campaigns yet" /> }}
            />
          </TabPane>

          {/* ── Tab 4: Contact Lists → Ads ─────────────────────── */}
          <TabPane
            tab={<span><TeamOutlined /> CRM Audience Targeting</span>}
            key="contacts"
          >
            <Alert
              type="info"
              showIcon
              icon={<TeamOutlined />}
              message="CRM Contact Lists → Google Ads"
              description="AI crafts ad copy and keyword strategy based on audience characteristics of your contact lists"
              style={{ marginBottom: 16, borderRadius: 12 }}
            />
            <Table
              dataSource={contactLists}
              rowKey="id"
              pagination={{ pageSize: 8 }}
              columns={[
                { title: 'ID', dataIndex: 'id', width: 60 },
                { title: 'List Name', dataIndex: 'name', ellipsis: true },
                { title: 'Members', dataIndex: 'member_count', width: 100 },
                {
                  title: 'Actions', width: 180,
                  render: (_, record) => (
                    <Button
                      type="primary"
                      size="small"
                      icon={<SendOutlined />}
                      disabled={!isConfigured}
                      onClick={() => openCreate('contact_list', record.id, record.name)}
                    >
                      Create Ad
                    </Button>
                  ),
                },
              ]}
              locale={{ emptyText: <Empty description="No contact lists yet" /> }}
            />
          </TabPane>

          {/* ── Tab 5: Scenarios → Ads ─────────────────────────── */}
          <TabPane
            tab={<span><AppstoreOutlined /> Scenario Content</span>}
            key="scenarios"
          >
            <Alert
              type="info"
              showIcon
              icon={<AppstoreOutlined />}
              message="Scenario AI Content → Google Ads"
              description="Turn AI-generated marketing content directly into Google Ads campaigns, from content to delivery in one flow"
              style={{ marginBottom: 16, borderRadius: 12 }}
            />
            <Table
              dataSource={scenarios}
              rowKey="id"
              pagination={{ pageSize: 8 }}
              columns={[
                { title: 'ID', dataIndex: 'id', width: 60 },
                { title: 'Scenario Name', dataIndex: 'name', ellipsis: true },
                {
                  title: 'Status', dataIndex: 'status', width: 100,
                  render: v => <Tag color={v === 'active' ? 'green' : v === 'draft' ? 'default' : 'blue'}>{v}</Tag>,
                },
                {
                  title: 'Actions', width: 180,
                  render: (_, record) => (
                    <Button
                      type="primary"
                      size="small"
                      icon={<SendOutlined />}
                      disabled={!isConfigured}
                      onClick={() => openCreate('scenario', record.id, record.name)}
                    >
                      Create Ad
                    </Button>
                  ),
                },
              ]}
              locale={{ emptyText: <Empty description="No scenarios yet" /> }}
            />
          </TabPane>

          {/* ── Tab 6: Integration Map ─────────────────────────── */}
          <TabPane
            tab={<span><LinkOutlined /> Integration Map</span>}
            key="map"
          >
            <div style={{ maxWidth: 900, margin: '0 auto' }}>
              <Title level={4} style={{ textAlign: 'center', marginBottom: 24 }}>
                Google Ads Deep Platform Integration Map
              </Title>
              <Row gutter={[16, 16]}>
                {[
                  {
                    icon: <AppstoreOutlined style={{ fontSize: 28, color: '#1890ff' }} />,
                    title: 'Scenarios',
                    desc: 'AI-generated content (copy/slides/EDM) directly converted into ad assets',
                    flow: 'Scenario content → AI-optimized copy → RSA search ads',
                    color: '#e6f7ff',
                  },
                  {
                    icon: <GlobalOutlined style={{ fontSize: 28, color: '#52c41a' }} />,
                    title: 'Landing Pages',
                    desc: 'Published landing pages automatically serve as ad destination URLs',
                    flow: 'LP published → AI content analysis → traffic-driving ads',
                    color: '#f6ffed',
                  },
                  {
                    icon: <MailOutlined style={{ fontSize: 28, color: '#fa8c16' }} />,
                    title: 'Email Marketing',
                    desc: 'Email campaign content becomes ads for cross-channel retargeting',
                    flow: 'EDM subject/content → AI conversion → search ads',
                    color: '#fff7e6',
                  },
                  {
                    icon: <TeamOutlined style={{ fontSize: 28, color: '#722ed1' }} />,
                    title: 'CRM Contacts',
                    desc: 'Audience analysis on lists to tailor keyword strategy and copy style',
                    flow: 'List analysis → AI audience targeting → precise delivery',
                    color: '#f9f0ff',
                  },
                  {
                    icon: <BulbOutlined style={{ fontSize: 28, color: '#eb2f96' }} />,
                    title: 'AI Engine',
                    desc: 'Claude AI auto-generates headlines, descriptions and keywords',
                    flow: 'Product info → Claude AI → optimized ad copy',
                    color: '#fff0f6',
                  },
                  {
                    icon: <BarChartOutlined style={{ fontSize: 28, color: '#13c2c2' }} />,
                    title: 'Funnel',
                    desc: 'Track ad conversions to optimize ROI and delivery strategy',
                    flow: 'Ad clicks → funnel tracking → conversion analysis',
                    color: '#e6fffb',
                  },
                ].map((item, idx) => (
                  <Col xs={24} sm={12} key={idx}>
                    <Card
                      size="small"
                      style={{ borderRadius: 12, background: item.color, height: '100%' }}
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Space>
                          {item.icon}
                          <Text strong style={{ fontSize: 16 }}>{item.title}</Text>
                        </Space>
                        <Text>{item.desc}</Text>
                        <div style={{
                          background: 'rgba(0,0,0,0.04)',
                          padding: '6px 12px',
                          borderRadius: 8,
                          fontSize: 13,
                        }}>
                          <ArrowRightOutlined style={{ marginRight: 4 }} />
                          {item.flow}
                        </div>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Divider />

              <Card style={{ borderRadius: 12, background: '#f0f5ff', textAlign: 'center' }}>
                <Title level={5}>Delivery Workflow</Title>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  {['Select Source Module', 'AI Generate Copy', 'Preview / Adjust', 'Create Campaign', 'Deliver / Track'].map((step, i) => (
                    <React.Fragment key={i}>
                      <Tag
                        color="blue"
                        style={{ fontSize: 14, padding: '6px 16px', borderRadius: 20 }}
                      >
                        {i + 1}. {step}
                      </Tag>
                      {i < 4 && <ArrowRightOutlined style={{ color: '#1890ff' }} />}
                    </React.Fragment>
                  ))}
                </div>
              </Card>
            </div>
          </TabPane>

          {/* ── Tab 7: Performance Report (SEM metrics) ─────────── */}
          <TabPane
            tab={<span><BarChartOutlined /> Performance Report</span>}
            key="performance"
          >
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <Alert
                type="info"
                showIcon
                icon={<BarChartOutlined />}
                message="Google Ads SEM Performance Report"
                description="Based on the ADCHARIYA Paid Media Performance Report format: overview, daily trends, campaign structure, auction insights"
                style={{ borderRadius: 12, flex: 1, minWidth: 260 }}
              />
              <Select
                value={dateRange}
                onChange={setDateRange}
                style={{ width: 180 }}
                options={[
                  { value: 'LAST_7_DAYS', label: 'Last 7 Days' },
                  { value: 'LAST_30_DAYS', label: 'Last 30 Days' },
                  { value: 'LAST_90_DAYS', label: 'Last 90 Days' },
                ]}
              />
              <Select
                value={sourceFilter}
                onChange={setSourceFilter}
                style={{ width: 200 }}
                placeholder="All sources"
                options={[
                  { value: 'all', label: 'All Sources' },
                  ...(performance?.totals?.by_source || []).map(s => ({
                    value: s.source,
                    label: s.source,
                  })),
                ]}
              />
            </div>

            {perfLoading ? (
              <div style={{ textAlign: 'center', padding: 60 }}>
                <Spin size="large" />
                <div style={{ marginTop: 16, color: '#999' }}>Loading performance data...</div>
              </div>
            ) : !performance || !performance.ok ? (
              <Result
                status="warning"
                title="Unable to fetch performance data"
                subTitle={(performance && performance.error) || 'Check Google Ads configuration or ensure the account has running campaigns'}
              />
            ) : (
              <div>
                {/* ── Overview KPI cards ── */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={12} sm={6}>
                    <Card hoverable style={{ borderRadius: 12, textAlign: 'center' }}>
                      <Statistic title="Impressions" value={performance.totals?.impressions || 0} suffix={<Text type="secondary" style={{ fontSize: 12 }}>Impressions</Text>} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card hoverable style={{ borderRadius: 12, textAlign: 'center' }}>
                      <Statistic title="Clicks" value={performance.totals?.clicks || 0} suffix={<Text type="secondary" style={{ fontSize: 12 }}>Clicks</Text>} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card hoverable style={{ borderRadius: 12, textAlign: 'center' }}>
                      <Statistic title="CTR" value={(performance.totals?.ctr || 0)} suffix="%" precision={2} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card hoverable style={{ borderRadius: 12, textAlign: 'center' }}>
                      <Statistic title="Total Cost" value={fmtMoney(performance.totals?.cost)} prefix="$" />
                    </Card>
                  </Col>
                </Row>

                {/* ── Per-source breakdown ── */}
                {(performance.totals?.by_source || []).length > 0 && (
                  <Card title="Performance by Source" style={{ borderRadius: 12, marginBottom: 24 }}>
                    <Table
                      rowKey="source"
                      dataSource={performance.totals.by_source}
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Source', dataIndex: 'source',
                          render: v => <SourceTag source={v} />,
                        },
                        {
                          title: 'Campaigns', dataIndex: 'campaigns',
                          width: 100, align: 'right',
                        },
                        {
                          title: 'Impressions', dataIndex: 'impressions',
                          width: 110, align: 'right', render: fmtNum,
                        },
                        {
                          title: 'Clicks', dataIndex: 'clicks',
                          width: 90, align: 'right', render: fmtNum,
                        },
                        {
                          title: 'CTR', dataIndex: 'ctr',
                          width: 80, align: 'right', render: fmtPct,
                        },
                        {
                          title: 'CPC', dataIndex: 'avg_cpc',
                          width: 90, align: 'right',
                          render: v => `$${fmtMoney(v)}`,
                        },
                        {
                          title: 'Cost', dataIndex: 'cost',
                          width: 100, align: 'right',
                          render: v => `$${fmtMoney(v)}`,
                        },
                        {
                          title: 'Conversions', dataIndex: 'conversions',
                          width: 100, align: 'right',
                          render: v => Number(v || 0).toFixed(1),
                        },
                      ]}
                    />
                  </Card>
                )}

                {/* ── Daily trend: impressions & clicks ── */}
                <Card title="Daily Impressions & Clicks" style={{ borderRadius: 12, marginBottom: 24 }}>
                  {performance.daily && performance.daily.length > 0 ? (
                    <Line
                      data={performance.daily.flatMap(d => [
                        { date: d.date, value: d.impressions, category: 'Impressions' },
                        { date: d.date, value: d.clicks, category: 'Clicks' },
                      ])}
                      xField="date"
                      yField="value"
                      seriesField="category"
                      height={280}
                      smooth
                      color={['#1a73e8', '#52c41a']}
                      legend={{ position: 'top' }}
                      axis={{ y: { title: { text: 'Count' } } }}
                    />
                  ) : (
                    <Empty description="No impressions/clicks data in this period" />
                  )}
                </Card>

                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  {/* ── Daily cost column ── */}
                  <Col xs={24} lg={14}>
                    <Card title="Daily Cost (USD)" style={{ borderRadius: 12, height: '100%' }}>
                      {performance.daily && performance.daily.length > 0 ? (
                        <Column
                          data={performance.daily.map(d => ({ date: d.date, value: d.cost }))}
                          xField="date"
                          yField="value"
                          height={260}
                          color="#fa8c16"
                          axis={{ y: { title: { text: 'USD' } } }}
                        />
                      ) : (
                        <Empty description="No cost data in this period" />
                      )}
                    </Card>
                  </Col>
                  {/* ── Auction insight ── */}
                  <Col xs={24} lg={10}>
                    <Card title="Auction Insight" style={{ borderRadius: 12, height: '100%' }}>
                      <Row gutter={[12, 12]}>
                        <Col span={24}>
                          <Card size="small" style={{ borderRadius: 10, background: '#f0f5ff' }}>
                            <Statistic
                              title="Search Impression Share"
                              value={fmtPct(performance.totals?.search_impression_share)}
                            />
                          </Card>
                        </Col>
                        <Col span={12}>
                          <Card size="small" style={{ borderRadius: 10, background: '#f6ffed' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Avg. CPC</Text>
                            <div style={{ fontSize: 20, fontWeight: 600 }}>${fmtMoney(performance.totals?.avg_cpc)}</div>
                          </Card>
                        </Col>
                        <Col span={12}>
                          <Card size="small" style={{ borderRadius: 10, background: '#fff7e6' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Conversions / CPA</Text>
                            <div style={{ fontSize: 20, fontWeight: 600 }}>
                              {(performance.totals?.conversions || 0).toFixed(1)}
                              <Text type="secondary" style={{ fontSize: 12 }}> / ${fmtMoney(performance.totals?.cost_per_conversion)}</Text>
                            </div>
                          </Card>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>

                {/* ── Campaign Structure ── */}
                <Card title="Campaign Structure" style={{ borderRadius: 12 }}>
                  <Table
                    rowKey="id"
                    dataSource={(performance.campaigns || []).filter(c => sourceFilter === 'all' || c.source === sourceFilter)}
                    pagination={{ pageSize: 8 }}
                    scroll={{ x: 1400 }}
                    size="small"
                    locale={{ emptyText: <Empty description="No campaigns for the selected source" /> }}
                    columns={[
                      {
                        title: 'Campaign', dataIndex: 'name', width: 260, render: (v, r) => (
                          <Space direction="vertical" size={2}>
                            <Text strong ellipsis style={{ maxWidth: 240 }}>{v}</Text>
                            <Space size={4}>
                              <SourceTag source={r.source} />
                              {r.property && (
                                <>
                                  <Tag color="blue">{`Property #${r.property.id}`}</Tag>
                                  <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                                    {(r.property.title || '').slice(0, 34)}
                                  </Text>
                                </>
                              )}
                            </Space>
                          </Space>
                        ),
                      },
                      { title: 'Source', dataIndex: 'source', width: 130, render: v => <SourceTag source={v} /> },
                      {
                        title: 'Status', dataIndex: 'status', width: 90,
                        render: v => {
                          const color = v === 'ENABLED' ? 'green' : v === 'PAUSED' ? 'orange' : v === 'REMOVED' ? 'red' : 'default';
                          return <Tag color={color}>{v}</Tag>;
                        },
                      },
                      { title: 'Type', dataIndex: 'channel_type', width: 90 },
                      { title: 'Impressions', dataIndex: 'impressions', width: 90, align: 'right', render: fmtNum },
                      { title: 'Clicks', dataIndex: 'clicks', width: 80, align: 'right', render: fmtNum },
                      { title: 'CTR', dataIndex: 'ctr', width: 80, align: 'right', render: fmtPct },
                      { title: 'CPC', dataIndex: 'avg_cpc', width: 90, align: 'right', render: v => `$${fmtMoney(v)}` },
                      { title: 'Cost', dataIndex: 'cost', width: 90, align: 'right', render: v => `$${fmtMoney(v)}` },
                      { title: 'Conversions', dataIndex: 'conversions', width: 80, align: 'right', render: v => Number(v || 0).toFixed(1) },
                      { title: 'CPA', dataIndex: 'cost_per_conversion', width: 90, align: 'right', render: v => (v && v > 0 ? `$${fmtMoney(v)}` : '-') },
                      { title: 'IS', dataIndex: 'search_impression_share', width: 80, align: 'right', render: fmtPct },
                    ]}
                  />
                </Card>
              </div>
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* ── Create Campaign Modal ────────────────────────────── */}
      <Modal
        title={
          <Space>
            <GoogleOutlined style={{ color: '#1a73e8' }} />
            Create Google Ads Campaign
          </Space>
        }
        open={createModal.open}
        onCancel={() => { setCreateModal({ ...createModal, open: false }); setCreateResult(null); }}
        footer={createResult ? [
          <Button key="close" onClick={() => { setCreateModal({ ...createModal, open: false }); setCreateResult(null); }}>
            Close
          </Button>,
        ] : [
          <Button key="cancel" onClick={() => setCreateModal({ ...createModal, open: false })}>
            Cancel
          </Button>,
          <Button
            key="create"
            type="primary"
            icon={<RocketOutlined />}
            loading={creating}
            onClick={handleCreate}
            style={{ background: 'linear-gradient(135deg, #1a73e8, #0d47a1)', border: 'none' }}
          >
            Generate Copy & Create Ad
          </Button>,
        ]}
        width={600}
      >
        {createResult ? (
          createResult.ok ? (
            <Result
              status="success"
              title="Campaign created successfully"
              subTitle={createResult.campaign_resource || `Source: ${createModal.sourceName}`}
              extra={
                <Space direction="vertical" style={{ width: '100%' }}>
                  {createResult.google_ads_url && (
                    <Button type="link" href={createResult.google_ads_url} target="_blank">
                      Go to Google Ads
                    </Button>
                  )}
                  {createResult.ai_generated_copy && (
                    <Card size="small" style={{ borderRadius: 8, textAlign: 'left' }}>
                      <Text strong>AI-generated ad copy:</Text>
                      <div style={{ marginTop: 8 }}>
                        {createResult.ai_generated_copy.headlines?.slice(0, 5).map((h, i) => (
                          <Tag key={i} color="blue" style={{ marginBottom: 4 }}>{h}</Tag>
                        ))}
                      </div>
                    </Card>
                  )}
                </Space>
              }
            />
          ) : (
            <Result
              status="error"
              title="Creation failed"
              subTitle={createResult.error}
            />
          )
        ) : (
          <div>
            <Alert
              type="info"
              showIcon
              message={`Source: ${createModal.sourceName}`}
              description={
                createModal.type === 'landing_page' ? 'AI will analyze the Landing Page content and auto-generate optimized search ad copy, descriptions and keywords' :
                createModal.type === 'email_campaign' ? 'AI will convert the email campaign subject and content into a Google Ads search campaign' :
                createModal.type === 'contact_list' ? 'AI will analyze audience characteristics and craft ad copy and keyword strategy' :
                'AI will convert the scenario AI content directly into Google Ads campaigns'
              }
              style={{ marginBottom: 16, borderRadius: 8 }}
            />
            {createModal.type === 'contact_list' && (
              <Form.Item label="Product/Service Description" style={{ marginBottom: 0 }}>
                <TextArea
                  rows={3}
                  placeholder="Describe your product or service. AI will combine audience insights to generate copy"
                  value={createModal.productInfo}
                  onChange={(e) => setCreateModal({ ...createModal, productInfo: e.target.value })}
                />
              </Form.Item>
            )}
            <div style={{ marginTop: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
              <Text type="secondary">
                <ThunderboltOutlined style={{ marginRight: 4 }} />
                After clicking Create, the system will:
              </Text>
              <ul style={{ marginTop: 8, paddingLeft: 20, color: '#666' }}>
                <li>Use Claude AI to analyze the source content</li>
                <li>Auto-generate 10-15 headlines + 3-4 descriptions</li>
                <li>Recommend 15-20 relevant search keywords</li>
                <li>Create a Google Ads RSA search campaign</li>
              </ul>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
